import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container } from "../styles";
import {
  Divider,
  Finished,
  FinishedPurchaseText,
  FinishedPurchase,
  Flex,
  FooterButton,
  Summary,
  SummaryCategory,
} from "./style";
import { FormattedMessage, useIntl } from "react-intl";
import { DotIcon } from "../../../ServicePanel/card-icons";
import { CheckmarkIcon } from "../components/Icons";
import { PaymentSummaryText, SummaryInfo } from "../Checkout/styles";
import { ProductInfoSummary } from "../components/ProductInfo";
import { formatCurrency } from "../Checkout/components/PaymentMethods/utils";
import { Subtotal } from "../Checkout/components/PaymentSummary";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import useNotification from "../../../../../hooks/newUXNotification";
import { resetSaleInfo } from "../../../../../redux/modules/staff-acess/actions";
import PrintableComponent from "./print-button";
import parsePhoneNumber from "libphonenumber-js";

const currentDate = new Date()
  .toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
  .replace(",", "");

export default function PurchaseSummary() {
  const intl = useIntl();
  const history = useHistory();
  const fantasyname = localStorage.getItem("serviceFantasyname");
  const contractNumber = localStorage.getItem("serviceContractNumber");
  const notification = useNotification();
  const { cartProducts, checkoutInfo, purchaseInfo, currency, CNPJ } =
    useSelector((state) => state.staff_access);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (cartProducts.length === 0 || !checkoutInfo || !purchaseInfo) {
      history.push("/staff-access/service-panel");
      notification.displayError(
        intl.formatMessage({
          id: "error_access_purchase_summary",
        }),
        "financial-Single-SignOn-info"
      );
    }
  }, [cartProducts, checkoutInfo, history, intl, notification, purchaseInfo]);

  const totals = useMemo(() => {
    const length = cartProducts.reduce((acc, curr) => acc + curr.qty, 0);
    const subtotal = cartProducts.reduce(
      (acc, curr) => acc + curr.value * curr.qty,
      0
    );
    const totalTaxes = (checkoutInfo?.taxes ?? []).reduce(
      (acc, curr) => acc + curr.value,
      0
    );

    return {
      subtotal: formatCurrency({
        value: subtotal,
        locale: intl.locale,
        currency,
      }),
      length,
      total: subtotal + totalTaxes,
    };
  }, [cartProducts, checkoutInfo, currency, intl.locale]);

  if (cartProducts.length === 0 || !checkoutInfo || !purchaseInfo) return null;

  return (
    <Container summary>
      <Header orderNumber={checkoutInfo.orderNumber} />
      <Summary>
        <ItemSummary cartProducts={cartProducts} />
        <Divider />
        <BuyerInfo
          document={purchaseInfo.document}
          name={purchaseInfo.name}
          phone={purchaseInfo.phone}
        />
        <Divider />
        <CompanyInfo
          CNPJ={CNPJ}
          contractNumber={contractNumber}
          name={fantasyname}
        />
        <Divider />
        <SaleInfo
          cardReader={purchaseInfo.cardReader}
          paymentMethod={purchaseInfo.paymentMethod}
          seller={user.name}
          brand={purchaseInfo.brand}
        />
        <Divider />
        <TaxBreakdownInfo taxes={checkoutInfo.taxes} />
        <Divider />
        <SubtotalInfo
          checkoutInfo={checkoutInfo}
          currency={currency}
          totals={totals}
        />
        <Footer />
      </Summary>
    </Container>
  );
}

function Header({ orderNumber }) {
  return (
    <FinishedPurchase>
      <Finished>
        <CheckmarkIcon />
      </Finished>
      <div>
        <FinishedPurchaseText type="title">
          <FormattedMessage id="purchase_completed" />
        </FinishedPurchaseText>
        <Flex gap="8px">
          <FinishedPurchaseText>
            <FormattedMessage
              id="purchase_order"
              values={{ number: orderNumber }}
            />
          </FinishedPurchaseText>
          <DotIcon color="#314158" />
          <FinishedPurchaseText>{currentDate}</FinishedPurchaseText>
        </Flex>
      </div>
    </FinishedPurchase>
  );
}

function ItemSummary({ cartProducts = [] }) {
  const intl = useIntl();
  return (
    <SummaryCategory>
      <PaymentSummaryText type="summary" mb="0">
        <FormattedMessage id="item_summary" />
      </PaymentSummaryText>

      {cartProducts.map((p) => (
        <ProductInfoSummary
          name={p.title[intl.locale]}
          qtd={p.qty}
          currency={p.currency}
          locale={intl.locale}
          value={p.value}
        />
      ))}
    </SummaryCategory>
  );
}

function BuyerInfo({ name, document, phone }) {
  const intl = useIntl();
  const phoneFormatted =
    phone.slice(0, 3) === "+55" && intl.locale === "pt"
      ? parsePhoneNumber(phone).formatNational()
      : parsePhoneNumber(phone).formatInternational();
  return (
    <SummaryCategory>
      <PaymentSummaryText type="summary" mb="0">
        <FormattedMessage id="buyer" />
      </PaymentSummaryText>

      <SummaryInfo>
        <PaymentSummaryText type="text" mb="0">
          <FormattedMessage id="name" />
        </PaymentSummaryText>
        <PaymentSummaryText type="value" mb="0">
          {name}
        </PaymentSummaryText>
      </SummaryInfo>

      <SummaryInfo>
        <PaymentSummaryText type="text" mb="0">
          <FormattedMessage id="document" />
        </PaymentSummaryText>
        <PaymentSummaryText type="value" mb="0">
          {document}
        </PaymentSummaryText>
      </SummaryInfo>

      <SummaryInfo>
        <PaymentSummaryText type="text" mb="0">
          <FormattedMessage id="phone2" />
        </PaymentSummaryText>
        <PaymentSummaryText type="value" mb="0">
          {phoneFormatted}
        </PaymentSummaryText>
      </SummaryInfo>
    </SummaryCategory>
  );
}

function CompanyInfo({ name, CNPJ, contractNumber }) {
  return (
    <SummaryCategory>
      <PaymentSummaryText type="summary" mb="0">
        <FormattedMessage id="company" />
      </PaymentSummaryText>

      <SummaryInfo>
        <PaymentSummaryText type="text" mb="0">
          <FormattedMessage id="name" />
        </PaymentSummaryText>
        <PaymentSummaryText type="value" mb="0">
          {name}
        </PaymentSummaryText>
      </SummaryInfo>

      <SummaryInfo>
        <PaymentSummaryText type="text" mb="0">
          <FormattedMessage id="CNPJ" />
        </PaymentSummaryText>
        <PaymentSummaryText type="value" mb="0">
          {CNPJ}
        </PaymentSummaryText>
      </SummaryInfo>

      <SummaryInfo>
        <PaymentSummaryText type="text" mb="0">
          <FormattedMessage id="tax_classification" />
        </PaymentSummaryText>
        <PaymentSummaryText type="value" mb="0">
          -
        </PaymentSummaryText>
      </SummaryInfo>

      <SummaryInfo>
        <PaymentSummaryText type="text" mb="0">
          <FormattedMessage id="contract_number2" />
        </PaymentSummaryText>
        <PaymentSummaryText type="value" mb="0">
          {contractNumber}
        </PaymentSummaryText>
      </SummaryInfo>
    </SummaryCategory>
  );
}

function SaleInfo({ seller, cardReader, paymentMethod, brand }) {
  return (
    <SummaryCategory>
      <PaymentSummaryText type="summary" mb="0">
        <FormattedMessage id="about_sale" />
      </PaymentSummaryText>

      <SummaryInfo>
        <PaymentSummaryText type="text" mb="0">
          <FormattedMessage id="seller" />
        </PaymentSummaryText>
        <PaymentSummaryText type="value" mb="0">
          {seller}
        </PaymentSummaryText>
      </SummaryInfo>

      {cardReader && (
        <SummaryInfo>
          <PaymentSummaryText type="text" mb="0">
            <FormattedMessage id="card_reader2" />
          </PaymentSummaryText>

          <PaymentSummaryText type="value" mb="0">
            {cardReader}
          </PaymentSummaryText>
        </SummaryInfo>
      )}
      {brand && (
        <SummaryInfo>
          <PaymentSummaryText type="text" mb="0">
            <FormattedMessage id="brand" />
          </PaymentSummaryText>
          <PaymentSummaryText type="value" mb="0">
            {brand}
          </PaymentSummaryText>
        </SummaryInfo>
      )}

      <SummaryInfo>
        <PaymentSummaryText type="text" mb="0">
          <FormattedMessage id="payment_method" />
        </PaymentSummaryText>
        <PaymentSummaryText type="value" mb="0">
          <FormattedMessage id={paymentMethod} />
        </PaymentSummaryText>
      </SummaryInfo>

      <SummaryInfo>
        <PaymentSummaryText type="text" mb="0">
          <FormattedMessage id="payment_status" />
        </PaymentSummaryText>
        <PaymentSummaryText type="value" mb="0">
          <FormattedMessage id="approved" />
        </PaymentSummaryText>
      </SummaryInfo>
    </SummaryCategory>
  );
}

function TaxBreakdownInfo({ taxes = [] }) {
  const intl = useIntl();
  return (
    <SummaryCategory>
      <PaymentSummaryText type="summary" mb="0">
        <FormattedMessage id="tax_breakdown" />
      </PaymentSummaryText>

      {taxes.map((t, i) => (
        <SummaryInfo key={i}>
          <PaymentSummaryText type="text">{t.label}</PaymentSummaryText>
          <PaymentSummaryText type="value">
            {formatCurrency({
              value: t.value,
              locale: intl.locale,
              currency: "BRL",
            })}
          </PaymentSummaryText>
        </SummaryInfo>
      ))}
    </SummaryCategory>
  );
}

function SubtotalInfo({ checkoutInfo, currency, totals }) {
  return (
    <SummaryCategory>
      <PaymentSummaryText type="summary" mb="0">
        <FormattedMessage id="sale_breakdown" />
      </PaymentSummaryText>

      <Subtotal
        checkoutInfo={checkoutInfo}
        currency={currency}
        totals={totals}
      />
    </SummaryCategory>
  );
}

function Footer() {
  const history = useHistory();
  const dispatch = useDispatch();

  const wipeData = useCallback(() => {
    dispatch(resetSaleInfo());
    history.push("/staff-access/service-panel");
  }, [history, dispatch]);

  const printData = useCallback(() => {
    history.push("/staff-access/service-panel");
  }, [history]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        wipeData();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [printData, wipeData]);

  return (
    <Flex gap="8px">
      <PrintableComponent />
      <FooterButton onClick={wipeData}>
        <FormattedMessage id="backToDashboard" />
        <span>(Esc)</span>
      </FooterButton>
    </Flex>
  );
}
